import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Search, Menu, X, ChevronRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api } from "@/api";
import logoBlack from "@/assets/ping-logo-black.png";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";

const NAV = [
  {
    label: "Equipment",
    base: "/equipment",
    cats: [
      { slug: "drivers", label: "Drivers" },
      { slug: "fairway-woods", label: "Fairway Woods" },
      { slug: "hybrids", label: "Hybrids" },
      { slug: "driving-irons", label: "Driving Irons" },
      { slug: "irons", label: "Irons" },
      { slug: "wedges", label: "Wedges" },
      { slug: "putters", label: "Putters" },
      { slug: "ladies", label: "Ladies" },
      { slug: "juniors", label: "Junior" },
    ],
  },
  {
    label: "Pro Shop",
    base: "/shop",
    cats: [
      { slug: "bags", label: "Bags" },
      { slug: "apparel", label: "Apparel" },
      { slug: "caps", label: "Caps" },
      { slug: "gloves", label: "Gloves" },
      { slug: "accessories", label: "Accessories" },
    ],
  },
];

export default function Header() {
  const { count } = useCart();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [announcement, setAnnouncement] = useState("J&B Golf · Sole Distributor for PING in India · Custom Fitting Available");

  useEffect(() => {
    api.get("/site-settings").then(({ data }) => {
      if (data?.announcement) setAnnouncement(data.announcement);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      const { data } = await api.get(`/products`, { params: { search: q } });
      setResults(data.slice(0, 6));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const linkFor = (p) => (p.group === "equipment" ? `/equipment/product/${p.slug}` : `/product/${p.slug}`);

  const goSearch = () => {
    if (results[0]) navigate(linkFor(results[0]));
    setShowSearch(false);
    setQ("");
  };

  return (
    <header
      className={`sticky top-0 z-50 bg-white btn-transition border-b ${scrolled ? "border-border shadow-[0_1px_0_rgba(0,0,0,0.06)]" : "border-transparent"}`}
      data-testid="site-header"
    >
      <div className="bg-[#0A0A0A] text-white text-center py-2 text-[11px] tracking-[0.2em] uppercase font-bold" data-testid="announcement-bar">
        {announcement}
      </div>

      <div className="container-ping flex items-center justify-between h-[70px]">
        <div className="flex items-center gap-4">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden" data-testid="mobile-menu-btn" aria-label="Menu">
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[300px] p-0 rounded-none overflow-auto">
              <div className="p-6 border-b flex items-baseline gap-2">
                <img src={logoBlack} alt="PING" className="h-6 w-auto" />
                <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">India</span>
              </div>
              <nav className="flex flex-col p-2">
                {NAV.map((group) => (
                  <div key={group.label} className="mb-2">
                    <p className="px-4 py-2 label-caps text-[#E31837]">{group.label}</p>
                    {group.cats.map((c) => (
                      <Link
                        key={c.slug}
                        to={`${group.base}/${c.slug}`}
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center justify-between px-4 py-2.5 text-sm font-semibold hover:bg-secondary btn-transition"
                        data-testid={`mobile-nav-${c.slug}`}
                      >
                        {c.label} <ChevronRight className="h-4 w-4" />
                      </Link>
                    ))}
                  </div>
                ))}
                <Link to="/technology" onClick={() => setMobileOpen(false)} className="px-4 py-3 label-caps hover:bg-secondary">Technology</Link>
                <Link to="/dealers" onClick={() => setMobileOpen(false)} className="px-4 py-3 label-caps hover:bg-secondary">Find a Fitting</Link>
                <Link to="/contact" onClick={() => setMobileOpen(false)} className="px-4 py-3 label-caps hover:bg-secondary">Contact</Link>
              </nav>
            </SheetContent>
          </Sheet>

          <Link to="/" className="flex items-baseline gap-2" data-testid="logo-link">
            <img src={logoBlack} alt="PING" className="h-7 md:h-8 w-auto" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground hidden sm:inline">India</span>
          </Link>
        </div>

        <nav className="hidden lg:flex items-center h-full" onMouseLeave={() => setOpenMenu(null)}>
          {NAV.map((item) => (
            <div key={item.label} className="h-full flex items-center" onMouseEnter={() => setOpenMenu(item.label)}>
              <Link
                to={item.base}
                className={`px-5 h-full flex items-center label-caps btn-transition border-b-2 ${openMenu === item.label ? "border-[#E31837]" : "border-transparent"}`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {item.label}
              </Link>
              {openMenu === item.label && (
                <div className="absolute left-0 right-0 top-full bg-white border-b border-border shadow-lg">
                  <div className="container-ping py-10 grid grid-cols-4 gap-x-6 gap-y-4">
                    {item.cats.map((c) => (
                      <Link
                        key={c.slug}
                        to={`${item.base}/${c.slug}`}
                        onClick={() => setOpenMenu(null)}
                        className="group flex items-center justify-between border-b border-border py-3 heading-3 text-lg hover:text-[#E31837] btn-transition"
                        data-testid={`mega-menu-${c.slug}`}
                      >
                        {c.label}
                        <ChevronRight className="h-4 w-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 btn-transition" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          <Link to="/technology" className="px-5 label-caps btn-transition hover:text-[#E31837]">Technology</Link>
          <Link to="/dealers" className="px-5 label-caps btn-transition hover:text-[#E31837]">Fitting</Link>
        </nav>

        <div className="flex items-center gap-5">
          <button onClick={() => setShowSearch((s) => !s)} data-testid="search-toggle" aria-label="Search">
            <Search className="h-5 w-5 hover:text-[#E31837] btn-transition" />
          </button>
          <Link to="/cart" className="relative" data-testid="cart-icon" aria-label="Cart">
            <ShoppingBag className="h-5 w-5 hover:text-[#E31837] btn-transition" />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#E31837] text-white text-[10px] font-bold h-4 min-w-4 px-1 flex items-center justify-center" data-testid="cart-count">
                {count}
              </span>
            )}
          </Link>
        </div>
      </div>

      {showSearch && (
        <div className="border-t border-border bg-white" data-testid="search-panel">
          <div className="container-ping py-5">
            <div className="flex items-center gap-3 border-b-2 border-[#0A0A0A] pb-2">
              <Search className="h-5 w-5" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && goSearch()}
                placeholder="Search clubs, bags, apparel..."
                className="flex-1 outline-none text-lg bg-transparent"
                data-testid="search-input"
              />
              <button onClick={() => { setShowSearch(false); setQ(""); }} aria-label="Close search">
                <X className="h-5 w-5" />
              </button>
            </div>
            {results.length > 0 && (
              <div className="mt-4 grid sm:grid-cols-2 gap-2" data-testid="search-results">
                {results.map((r) => (
                  <Link
                    key={r.id}
                    to={linkFor(r)}
                    onClick={() => { setShowSearch(false); setQ(""); }}
                    className="flex items-center gap-3 p-2 hover:bg-secondary btn-transition"
                  >
                    <img src={r.image} alt={r.name} className="w-12 h-12 object-cover" />
                    <div>
                      <p className="font-semibold text-sm">{r.name}</p>
                      <p className="text-xs text-muted-foreground">{r.category}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
